import { useEffect, useState } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const heroSlides = [
  {
    image: "https://customer-assets.emergentagent.com/job_nominate-now/artifacts/pmyip1ov_slider-image.jpg",
    alt: "Golden Tree Awards 2025"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_nominate-now/artifacts/fyuefv15_slider-image2.jpg",
    alt: "World AI Technology Expo"
  }
];

export const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Auto-slide every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image Slider */}
      <div className="absolute inset-0">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#0a0a0a]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />

      {/* Slider Navigation Arrows */}
      <button
        onClick={prevSlide}
        data-testid="hero-prev-slide"
        className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 border border-white/30 flex items-center justify-center text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300 bg-black/20 backdrop-blur-sm"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        onClick={nextSlide}
        data-testid="hero-next-slide"
        className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 border border-white/30 flex items-center justify-center text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300 bg-black/20 backdrop-blur-sm"
      >
        <ChevronRight size={24} />
      </button>

      {/* Slider Indicators */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            data-testid={`hero-slide-indicator-${index}`}
            className={`h-1 transition-all duration-300 ${
              index === currentSlide ? 'w-10 bg-[#D4AF37]' : 'w-6 bg-white/30'
            }`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 sm:px-8 lg:px-16 text-center pt-20">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Subtext */}
          <p className="font-google-sans text-[#D4AF37] text-base sm:text-lg md:text-xl tracking-wide mb-6">
            Creating Unforgettable Events & Experiences
          </p>

          {/* Main Headline */}
          <h1 className="font-montserrat text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white leading-tight mb-8 font-bold">
            World's Biggest Conference &<br />
            <span className="gradient-gold">Events Management Company</span>
          </h1>

          {/* Description */}
          <p className="font-google-sans text-[#A1A1AA] text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            From award ceremonies to corporate conferences, we bring your vision to life 
            with world-class event management services across 20+ industries globally.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="group bg-[#D4AF37] text-black font-medium px-8 py-4 uppercase tracking-widest text-sm hover:bg-[#C5A028] transition-all duration-300 flex items-center gap-3"
            >
              Our Services
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact-us"
              data-testid="hero-contact-btn"
              className="group border border-white/30 text-white font-medium px-8 py-4 uppercase tracking-widest text-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all duration-300 flex items-center gap-3"
            >
              Get In Touch
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
